import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import Ticket from '../../../interfaces/ticket/Ticket';
import './IssuesPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faBug, faCode, faArrowUp, faArrowDown, faAngleUp, faAnglesUp, faAngleDown, faAnglesDown, faEquals, faObjectUngroup } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import TagBadge from '../../ui/Tag/TagBadge';
import AvatarImage from '../../ui/Avatar/AvatarImage';
import defaultAvatar from '../../../assets/images/defaultavtarimg.svg';

interface TicketCardProps {
    ticket: Ticket;
    index: number;
    sprintView: boolean;
    onCardClick: (ticketId: string) => void;
}

const TicketCard: React.FC<TicketCardProps> = ({ ticket, index, sprintView = false, onCardClick }) => {
    const navigate = useNavigate();

    const getStatusBaseColor = (statusName?: string) => {
        switch (statusName?.toLowerCase()) {
            case 'closed': return '#6c757d'; // Bootstrap secondary gray
            case 'in progress': return '#007bff'; // Bootstrap primary blue
            case 'ready for qa': return '#ffc107'; // Bootstrap warning yellow
            case 'in review': return '#ffc107'; // Bootstrap warning yellow
            case 'awaiting release': return '#7e57c2'; // Bootstrap success green
            case 'done': return '#28a745'; // Bootstrap success green
            case 'awaiting release': return '#7e57c2'; // Bootstrap success green
            case 're-opened': return '#dc3545'; // Bootstrap danger red
            default: return '#f8f9fa'; // Bootstrap light gray
        }
    };

    const getPriorityIcon = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'highest':
                return <FontAwesomeIcon icon={faAnglesUp} className="text-red-500" />;
            case 'high':
                return <FontAwesomeIcon icon={faAngleUp} className="text-orange-500" />;
            case 'medium':
                return <FontAwesomeIcon icon={faEquals} className="text-yellow-500" />;
            case 'low':
                return <FontAwesomeIcon icon={faAngleDown} className="text-green-500" />;
            case 'lowest':
                return <FontAwesomeIcon icon={faAnglesDown} className="text-green-500" />;
            default:
                return null;
        }
    }

    return (
        <Draggable draggableId={ticket.id} index={index}>
            {(provided, snapshot) => (
                <div
                    title={ticket.title}
                    onClick={() => onCardClick(ticket.publicId)}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`ticket-card ${snapshot.isDragging ? 'is-dragging' : ''}`}
                >

                    {sprintView ? (
                        <div className="sprint-ticket-card-content">
                            <div className="sprint-ticket-card-header">
                                <span className="ticket-card-id">{ticket.publicId}</span>
                                <div className="ticket-type-icons flex items-center gap-2">
                                    {ticket.tags?.some(tag => tag.name.toLowerCase() === "bug") && (<FontAwesomeIcon className="text-red-500" icon={faBug} />)}
                                    {ticket.tags?.some(tag => tag.name.toLowerCase() === "story") && (<FontAwesomeIcon className="text-green-500" icon={faBookmark} />)}
                                                                         {ticket.tags?.some(tag => tag.name.toLowerCase() === "task") && (<FontAwesomeIcon className="text-blue-500" icon={faCode} />)}
                                                                         {ticket.tags?.some(tag => tag.name.toLowerCase() === "ui") && (<FontAwesomeIcon className="text-purple-500" icon={faObjectUngroup} />)}
                                                                     </div>                            </div>
                            <div className="sprint-ticket-card-title">
                                <span className={`ZZz${(ticket.status.toLowerCase() === "done" || ticket.status.toLowerCase() === "closed") && 'line-through text-muted'}`}>{ticket.title}</span>
                            </div>
                            <div className="sprint-ticket-card-footer">
                                <div className='flex items-center gap-2'>
                                    {getPriorityIcon(ticket.priority)}
                                    {ticket.storyPoints != null && <TagBadge name={ticket.storyPoints.toString()} color="#dcdbdbff" />}
                                </div>
                                <div className="ticket-card-avatars">
                                    {ticket.assignee ? (
                                        <div className="avatar-wrapper" title={`Assignee: ${ticket.assignee.displayName}`}>
                                            <AvatarImage size='xxs' userId={ticket.assignee.id} />
                                        </div>
                                    ) : (
                                        <div className="avatar-wrapper" title={`Unassigned`}>
                                            <AvatarImage size='xxs' userId={''} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className={`ticket-card-header`}>
                                <span className="ticket-card-id">{ticket.publicId}</span>
                                <div className="ticket-card-title flex align-middle items-center gap-2">
                                    {ticket.tags?.some(tag => tag.name.toLowerCase() === "bug") && (<FontAwesomeIcon className="text-red-500" icon={faBug} />)}
                                                                         {ticket.tags?.some(tag => tag.name.toLowerCase() === "story") && (<FontAwesomeIcon className="text-green-500" icon={faBookmark} />)}
                                                                         {ticket.tags?.some(tag => tag.name.toLowerCase() === "task") && (<FontAwesomeIcon className="text-blue-500" icon={faCode} />)}
                                                                         {ticket.tags?.some(tag => tag.name.toLowerCase() === "ui") && (<FontAwesomeIcon className="text-purple-500" icon={faObjectUngroup} />)}
                                                                         <span className={`text-sm ticket-title-truncate ${(ticket.status.toLowerCase() === "done" || ticket.status.toLowerCase() === "closed") && 'line-through text-muted'}`}>{ticket.title}</span>                                </div>
                            </div>
                            <div className="ticket-card-body">
                                <TagBadge name={ticket.status || 'N/A'} color={getStatusBaseColor(ticket.status)} />
                                <div className='flex items-center gap-4'>
                                    {getPriorityIcon(ticket.priority)}
                                    {ticket.storyPoints != null ? <TagBadge name={ticket.storyPoints.toString()} color="#dcdbdbff" /> : <TagBadge name={"-"} color="#dcdbdbff" />}
                                    <div className="ticket-card-avatars">
                                        {ticket.assignee ? (
                                            <div className="avatar-wrapper" title={`Assignee: ${ticket.assignee.displayName}`}>
                                                <AvatarImage size='xxs' userId={ticket.assignee.id} />
                                            </div>
                                        ) : (
                                            <div className="avatar-wrapper" title={`Unassigned`}>
                                                <AvatarImage size='xxs' userId={''} />
                                            </div>
                                        )
                                        }
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </Draggable>
    );
};

export default TicketCard;
